import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useActor, useSelector } from '@xstate/react';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, Input, LoaderBalloon, RadioGroup, Select, SkeletonLine, Tooltip } from '@crazyegginc/hatch';

import { useSite, useMutation, useNotifications } from '/src/hooks';
import Wizard from '/src/components/wizard/Wizard';
import { ValueElementSelector } from './value/ValueElementSelector';
import { CurrencySelector } from '/src/components/CurrencySelector';
import { WebP } from '/src/components/WebP';
import { CodeBox } from '/src/components/CodeBox';

import { goalEditMutation } from '/src/features/goals/mutations';
import { goalDetailQuery } from '/src/features/goals/queries';

import { normalizeUrl } from '/src/utils/url';
import { generateCodeGoalScript } from '../utils';
import {
  GOAL_TRACKING_OPTIONS,
  ELEMENT_SELECTOR_TYPES,
  GOAL_TRIGGERS_METADATA,
  GOAL_VALUE_TYPES,
  GOAL_TRIGGER_TYPES,
  GOAL_VALUE_TYPE_OPTIONS,
} from '/src/features/goals/constants';

import { ReactComponent as ExpandIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg';
import { ReactComponent as WarningIcon } from '@crazyegginc/hatch/dist/images/icon-warning-circle-filled.svg';

const selectTriggers = (state) => state.context?.payload?.triggers;
const selectGoal = (state) => state.context.payload;

export function goalTriggerValue(trigger) {
  if (trigger.valueType === GOAL_VALUE_TYPES.CODE) {
    return {
      valueType: GOAL_VALUE_TYPES.CODE,
      fixedValue: null,
      elementValue: null,
    };
  }

  if (trigger.valueType === GOAL_VALUE_TYPES.FIXED) {
    return {
      valueType: GOAL_VALUE_TYPES.FIXED,
      elementValue: null,
      fixedValue: {
        currency: trigger.fixedValue.currency,
        worth: trigger.fixedValue.worth,
      },
    };
  }

  if (trigger.valueType === GOAL_VALUE_TYPES.ELEMENT) {
    return {
      valueType: GOAL_VALUE_TYPES.ELEMENT,
      elementValue: {
        currency: trigger.elementValue.currency,
        condition: {
          trackingOption: trigger.elementValue.condition.trackingOption,
          elementSelectorType: trigger.elementValue.condition.elementSelectorType,
          pageUrl: normalizeUrl(trigger.elementValue.condition.pageUrl),
          onPage:
            !!trigger.elementValue.condition.pageUrl &&
            trigger.elementValue.condition.trackingOption === GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE
              ? normalizeUrl(trigger.elementValue.condition.pageUrl)
              : trigger.elementValue.condition.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD
                ? normalizeUrl(trigger.elementValue.condition.wildcardUrl, { appendSlash: false })
                : null,
          onAnyPage: trigger.elementValue.condition.trackingOption === GOAL_TRACKING_OPTIONS.ANY_PAGE ? true : null,
          wildcardUrl:
            !!trigger.elementValue.condition.wildcardUrl &&
            trigger.elementValue.condition.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD
              ? trigger.elementValue.condition.wildcardUrl
              : null,
          selectorForDesktop: trigger.elementValue.condition.selectorForDesktop,
          selectorForTablet: trigger.elementValue.condition.selectorForTablet,
          selectorForPhone: trigger.elementValue.condition.selectorForPhone,
        },
      },
      fixedValue: null,
    };
  }
  if (trigger.valueType === GOAL_VALUE_TYPES.NO_VALUE) {
    return {
      valueType: null,
      fixedValue: null,
      elementValue: null,
    };
  }
}

export function GoalValueWizard({ service }) {
  const notifications = useNotifications();
  const [state, send] = useActor(service);
  const { goalId } = useParams();

  const triggers = useSelector(service, selectTriggers);
  const activeTriggers = triggers.filter((t) => !t.deletedAt);

  const { mutate: mutateGoalEdit } = useMutation(goalEditMutation);

  const { refetch } = useQuery({
    ...goalDetailQuery({
      id: parseInt(goalId),
    }),
    enabled: false,
    keepPreviousData: false,
    cacheTime: 0,
    networkMode: 'always',
  });

  const { isEditing } = state.context;

  useEffect(() => {
    const subscription = service.subscribe(async (state) => {
      if (state.matches('initializeValue')) {
        const result = await refetch();

        if (result.isSuccess) {
          send({ type: 'GOAL_LOADED', value: result.data.goalDetail });
        }
      } else if (state.matches('savingValue')) {
        const { autoName, name, triggers, syncValues = false } = state.context.payload;
        const activeFilters = triggers.filter((t) => !t.deletedAt);
        const { goalId } = state.context;
        mutateGoalEdit(
          {
            params: {
              id: parseInt(goalId),
              name,
              autoName: autoName || name,
              syncValues,
              triggers: activeFilters.map((trigger) => {
                return {
                  id: trigger.id,
                  autoName: trigger.autoName,
                  name: trigger.name,
                  filter: trigger.filter,
                  ...goalTriggerValue(syncValues ? activeFilters[0] : trigger),
                };
              }),
            },
          },
          {
            onSuccess: (data) => {
              notifications.success({ content: 'Conversion updated successfully.', timeout: 3000 });
              service.send({ type: 'VALUE_SUCCESS', goalId: data.goalEdit.id });
            },
            onError: (error) => {
              notifications.error({ content: 'Conversion update failed.', timeout: 3000, context: { error } });
              service.send('VALUE_ERROR');
            },
          },
        );
      }
    });

    return subscription.unsubscribe;
  }, [refetch, service, send, mutateGoalEdit, notifications]);

  const onlyEcommerceOrApiTriggers = activeTriggers.every((trigger) => {
    return trigger.trigger === GOAL_TRIGGER_TYPES.API || trigger.trigger === GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT;
  });

  const isNextEnabled = service.initialized ? service.nextState({ type: 'NEXT' }).changed : false;

  return (
    <Wizard>
      <Wizard.Header>
        <Wizard.CloseButton onClick={() => send('CANCEL')} />
      </Wizard.Header>
      <Wizard.Content dontMask={true}>
        <div className="mx-auto max-w-[740px] flex flex-col items-center">
          <Wizard.Title>
            {onlyEcommerceOrApiTriggers ? 'Value of your Conversion' : 'Set the value of this conversion (optional)'}
          </Wizard.Title>

          <GoalValueContent service={service} />
        </div>
      </Wizard.Content>
      <Wizard.SimpleFooter
        currentStep={isEditing ? 1 : 2}
        totalSteps={isEditing ? 1 : 2}
        showMeta={!isEditing}
        title="Conversion value"
        cancelText="Cancel"
        nextEnabled={isNextEnabled}
        onBack={() => send('BACK')}
        onNext={() => send('NEXT')}
        onCancel={() => send('CANCEL')}
        confirmCancel={false}
      />
    </Wizard>
  );
}

function GoalValueContent({ service }) {
  const [state] = useActor(service);
  const triggers = useSelector(service, selectTriggers);
  const activeTriggers = triggers.filter((t) => !t.deletedAt);

  const isLoading = state.matches('initializeValue');

  const hasApiTrigger = activeTriggers.some((t) => t.trigger === GOAL_TRIGGER_TYPES.API);
  const haveEcommerceTrigger = activeTriggers.some((trigger) => trigger.trigger === GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT);
  const onlyEcommerceOrApiTriggers = activeTriggers.every((trigger) => {
    return trigger.trigger === GOAL_TRIGGER_TYPES.API || trigger.trigger === GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT;
  });
  let text = '';
  if (onlyEcommerceOrApiTriggers) {
    if (hasApiTrigger && haveEcommerceTrigger) {
      text = 'API and eCommerce';
    } else {
      text = hasApiTrigger ? 'API' : 'eCommerce';
    }
  }

  return (
    <>
      <p className="mb-10 text-center">
        {onlyEcommerceOrApiTriggers ? (
          <>
            Because you only have {text} Trigger(s) the Conversion Value will be set automatically from those
            trigger(s).
          </>
        ) : (
          <>
            Add a value to this conversion so you can track total conversion value over time.
            <br />
            You can update this anytime.
          </>
        )}
      </p>

      <div className="mx-auto max-w-[740px] w-full">
        {isLoading ? (
          <div className="mt-10">
            <LoaderBalloon />
          </div>
        ) : (
          !onlyEcommerceOrApiTriggers && <GoalValueForm service={service} />
        )}
      </div>
    </>
  );
}

function GoalValueForm({ service }) {
  const [, send] = useActor(service);
  const goal = useSelector(service, selectGoal);
  const triggers = useSelector(service, selectTriggers).filter((t) => !t.deletedAt);
  const [openedCard, setOpenedCard] = useState(() => triggers.length - 1);
  const hasMoreThanOneTrigger = triggers.length > 1;
  const hasEcommerceOrApiTrigger = triggers?.some?.((t) =>
    [GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT, GOAL_TRIGGER_TYPES.API].includes(t.trigger),
  );

  return (
    <div>
      {triggers && triggers.length > 1 ? (
        <div className="flex justify-start">
          <Tooltip
            tooltipContent={
              <div className="max-w-md">
                Ecommerce and API triggers have their value set automatically. You can optionally specify values for
                your other triggers.
              </div>
            }
          >
            <Checkbox
              checked={!goal.syncValues}
              value={!goal.syncValues}
              onChange={(e) => {
                send({
                  type: 'SET_SYNC_VALUES',
                  value: !e.target.checked,
                });
              }}
              id="syncValues"
              label="Set different values for each trigger"
              disabled={hasEcommerceOrApiTrigger}
            />
          </Tooltip>
        </div>
      ) : null}

      {goal.syncValues || !hasMoreThanOneTrigger ? (
        <h2 className="text-header-2 mt-[30px]">How do you want to set the value?</h2>
      ) : null}

      {!goal.syncValues && hasMoreThanOneTrigger ? (
        <ul className="mt-[30px] flex flex-col gap-5" data-testid="value-trigger-cards">
          {goal.triggers.map((trigger, index) => {
            if (trigger.deletedAt) return null;
            const isApi = trigger.trigger === GOAL_TRIGGER_TYPES.API;
            const isEcommerce = trigger.trigger === GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT;
            return (
              <li key={`GoalValue:${index}`} data-testid="value-trigger-card">
                <TriggerCard
                  service={service}
                  index={index}
                  onToggle={() => {
                    setOpenedCard((current) => (current === index ? null : index));
                  }}
                  expanded={index === openedCard}
                >
                  {isApi
                    ? 'API triggers have their value set automatically as part of the API event. You can not edit them.'
                    : isEcommerce
                      ? 'eCommerce triggers have their value set automatically based on the eCommerce event. You can not edit them.'
                      : renderForm(trigger.valueType, { index, trigger, service })}
                </TriggerCard>
              </li>
            );
          })}
        </ul>
      ) : (
        renderForm(goal.triggers?.[0]?.valueType || GOAL_VALUE_TYPES.NO_VALUE, {
          trigger: { ...(goal.triggers?.[0] ?? { valueType: GOAL_VALUE_TYPES.NO_VALUE }) },
          forceUniversalCodeValue: hasMoreThanOneTrigger ? true : false,
          service,
          index: null,
        })
      )}
    </div>
  );
}

function renderForm(type, props) {
  switch (type) {
    case GOAL_VALUE_TYPES.FIXED:
      return <FixedValueForm {...props} />;
    case GOAL_VALUE_TYPES.ELEMENT:
      return <ElementValueForm {...props} />;
    case GOAL_VALUE_TYPES.CODE:
      return <CodeValueForm {...props} />;
    default:
      return <NoValueForm {...props} />;
  }
}

function getTriggerValue(trigger) {
  return trigger?.[`${trigger?.valueType?.toLowerCase?.()}Value`] || {};
}

function GoalValueCurrencySelector({ trigger, service, index, ...rest }) {
  const [, send] = useActor(service);
  const triggerValue = useMemo(() => getTriggerValue(trigger), [trigger]);

  return (
    <CurrencySelector
      value={triggerValue.currency}
      onChange={(value) => {
        send({ type: 'SET_TRIGGER_VALUE', index, trigger, key: 'currency', value });
      }}
      unitOnly={true}
      {...rest}
    />
  );
}

function GoalValueTypeSelector({ service, trigger, index }) {
  const [, send] = useActor(service);

  useEffect(() => {
    if (!trigger.valueType) {
      send({ type: 'SET_TRIGGER', index, key: 'valueType', value: GOAL_VALUE_TYPES.NO_VALUE });
    }
  }, [trigger.valueType, send, index]);

  return (
    <div
      className={classNames('w-1/2', {
        'mt-[25px]': index == null,
      })}
    >
      <Select
        options={GOAL_VALUE_TYPE_OPTIONS}
        value={trigger.valueType}
        onChange={(value) => {
          send({ type: 'SET_TRIGGER', index, key: 'valueType', value });
        }}
        label={index !== null ? 'Set value by' : null}
        data-testid={index === null ? 'value-type-select' : null}
      />
    </div>
  );
}

function NoValueForm({ service, trigger, index }) {
  return (
    <div className="flex items-end gap-5">
      <GoalValueTypeSelector trigger={trigger} service={service} index={index} />
      <div className="w-1/2" />
    </div>
  );
}

function FixedValueForm({ service, trigger, index }) {
  const [state, send] = useActor(service);
  const errors = state.context.errors?.triggers?.[index ?? 0];
  const [touched, setTouched] = useState(errors?.fixedValue?.worth && trigger.fixedValue?.worth ? true : false);

  return (
    <div className="flex items-start gap-5">
      <GoalValueTypeSelector trigger={trigger} service={service} index={index} />
      <div className="w-1/4">
        <Input
          value={trigger.fixedValue?.worth || ''}
          onChange={(e) => {
            const { value } = e.target;
            setTouched(true);
            send({ type: 'SET_TRIGGER_VALUE', index, trigger, key: 'worth', value });
          }}
          onBlur={() => setTouched(true)}
          id="valueMetric"
          label="Value"
          placeholder="eg. 10"
          error={touched ? errors?.fixedValue?.worth : null}
        />
      </div>
      <div className="w-1/4">
        <GoalValueCurrencySelector
          trigger={trigger}
          service={service}
          index={index}
          id="valueCurrency"
          label="Currency"
        />
      </div>
    </div>
  );
}

function ElementValueForm({ service, trigger, index }) {
  const [state, send] = useActor(service);
  const errors = state.context.errors?.triggers?.[index ?? 0];
  const triggerValue = useMemo(() => getTriggerValue(trigger), [trigger]);
  const [touched, setTouched] = useState({
    pageUrl: errors?.elementValue?.condition?.pageUrl && triggerValue?.condition?.pageUrl ? true : false,
    wildcardUrl: errors?.elementValue?.condition?.wildcardUrl && triggerValue?.condition?.wildcardUrl ? true : false,
  });

  const elementSelectorReady =
    !!triggerValue?.condition?.pageUrl &&
    !!triggerValue?.condition?.trackingOption &&
    !!triggerValue?.condition?.elementSelectorType;

  return (
    <div className="mt-6 flex flex-col gap-5">
      <GoalValueTypeSelector trigger={trigger} service={service} index={index} />

      <h2 className="text-header-2 mt-5">What page is the element on?</h2>
      <Input
        data-testid="element-value-page-url"
        value={triggerValue?.condition?.pageUrl ?? ''}
        onChange={(e) => {
          const { value } = e.target;
          setTouched((x) => ({ ...x, pageUrl: true }));
          send({
            type: 'SET_TRIGGER_VALUE',
            index,
            trigger,
            key: 'condition',
            value: { ...(triggerValue.condition || {}), pageUrl: value },
          });
        }}
        onBlur={() => setTouched((x) => ({ ...x, pageUrl: true }))}
        error={touched.pageUrl ? errors?.elementValue?.condition?.pageUrl : null}
      />

      <RadioGroup
        options={[
          { value: GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE, label: 'Track this element only on this page.' },
          {
            value: GOAL_TRACKING_OPTIONS.ANY_PAGE,
            label: 'Include this element from any page across my website.',
          },
          {
            value: GOAL_TRACKING_OPTIONS.WILDCARD,
            label: 'Include this element for any pages that match a wildcard.',
          },
        ]}
        value={triggerValue?.condition?.trackingOption}
        onChange={(value) => {
          send({
            type: 'SET_TRIGGER_VALUE',
            index,
            trigger,
            key: 'condition',
            value: {
              ...(triggerValue.condition || {}),
              trackingOption: value,
              onPage: value !== GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE ? undefined : triggerValue.condition.pageUrl,
              onAnyPage: value !== GOAL_TRACKING_OPTIONS.ANY_PAGE ? undefined : true,
              wildcardUrl: value !== GOAL_TRACKING_OPTIONS.WILDCARD ? undefined : triggerValue.condition.wildcardUrl,
            },
          });
        }}
      />

      {triggerValue?.condition?.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD ? (
        <Input
          data-testid="element-value-wildcard-url"
          placeholder={`Enter a wildcard pattern (e.g. ${triggerValue?.condition?.pageUrl}/*)`}
          value={triggerValue?.condition?.wildcardUrl ?? ''}
          onChange={(e) => {
            setTouched((x) => ({ ...x, wildcardUrl: true }));
            send({
              type: 'SET_TRIGGER_VALUE',
              index,
              trigger,
              key: 'condition',
              value: {
                ...(triggerValue.condition || {}),
                wildcardUrl: e.target.value,
              },
            });
          }}
          onBlur={() => setTouched((x) => ({ ...x, wildcardUrl: true }))}
          error={touched.wildcardUrl ? errors?.elementValue?.condition?.wildcardUrl : null}
        />
      ) : null}

      <h2 className="text-header-2 mt-5">What is the currency of the element?</h2>
      <GoalValueCurrencySelector
        trigger={trigger}
        service={service}
        index={index}
        data-testid="element-value-currency"
      />

      <h2 className="text-header-2">Select the element</h2>

      <RadioGroup
        options={[
          { value: ELEMENT_SELECTOR_TYPES.SPECIFIC, label: 'Track only the element I click.' },
          // https://app.shortcut.com/crazyegg/story/27554/broad-element-matching-for-goal-triggers
          // { value: 'BROAD', label: 'Track all similar elements' },
          { value: ELEMENT_SELECTOR_TYPES.MANUAL, label: 'Advanced: Track via CSS selector.' },
        ]}
        value={triggerValue?.condition?.elementSelectorType}
        onChange={(value) => {
          send({
            type: 'SET_TRIGGER_VALUE',
            index,
            trigger,
            key: 'condition',
            value: { ...(triggerValue.condition || {}), elementSelectorType: value },
          });
        }}
      />

      <div className="text-body-4 mb-2.5 mt-2.5">
        The element you select will be outlined in red. To cancel a selection, click on the element again.
      </div>

      {elementSelectorReady ? (
        <ValueElementSelector
          isManual={triggerValue?.condition?.elementSelectorType === ELEMENT_SELECTOR_TYPES.MANUAL}
          trigger={trigger}
          service={service}
          index={index}
          errors={errors?.elementValue?.condition}
        />
      ) : null}
    </div>
  );
}

function CodeValueForm({ service, trigger, index, forceUniversalCodeValue = false }) {
  const [state] = useActor(service);
  const { goalId } = state.context;

  const code = useMemo(() => {
    if (trigger.trigger !== GOAL_TRIGGER_TYPES.CODE || forceUniversalCodeValue) {
      return `CE2.nextGoalConversionValue(\n\t${goalId},\n\t{ worth: '99.95', currency: 'USD' }\n);`;
    } else {
      return generateCodeGoalScript(trigger, trigger.code);
    }
  }, [trigger, goalId, forceUniversalCodeValue]);

  return (
    <div className="mt-6 flex flex-col gap-5">
      <GoalValueTypeSelector trigger={trigger} service={service} index={index} />
      <h2 className="text-header-2 mt-5">Insert this code snippet onto your site</h2>
      <p className="text-body-2">Paste this code snippet inside the &lt;head&gt; tag of your site.</p>
      <CodeBox loading={false} canCopy={true} code={code} height="200px" />
    </div>
  );
}

function TriggerCard({ service, index, children, onToggle, expanded = false }) {
  const [state] = useActor(service);
  const { selectedSite } = useSite();

  const trigger = useMemo(() => state.context.payload.triggers[index], [state.context.payload.triggers, index]);

  const metadata = useMemo(() => GOAL_TRIGGERS_METADATA[trigger.trigger], [trigger?.trigger]);

  const target = useMemo(() => {
    return selectedSite?.name;
  }, [selectedSite?.name]);

  const valid = !state.context.errors?.triggers?.[index];

  return (
    <div className="rounded border border-mystic-500 shadow">
      <div
        className={classNames('flex justify-between p-6 !pb-5', expanded && 'border-b border-dashed border-mystic-500')}
      >
        <div className="flex">
          <WebP
            className="-ml-2 w-[35px]"
            webp={metadata.webp}
            fallback={metadata.png}
            width="35"
            height="auto"
            alt="trigger"
          />
          <div className="ml-3 flex flex-col">
            <div className="flex space-x-3 items-center">
              <h3 className="text-header-2">{metadata.selectLabel}</h3>
              {!valid && !expanded ? (
                <Tooltip
                  tooltipContent={<div className="w-40 text-left">This trigger has invalid or missing values.</div>}
                >
                  <WarningIcon
                    className="mb-[2px] h-[16px] w-[16px] fill-current text-radical-red-500"
                    aria-label="trigger-validation-error"
                  />
                </Tooltip>
              ) : null}
            </div>
            <span className="text-body-2 flex items-center gap-1">
              on{' '}
              {target ? (
                <a href={`http://${target}`} target="_blank" rel="noreferrer" className="text-link">
                  {target}
                </a>
              ) : (
                <SkeletonLine />
              )}
            </span>
          </div>
        </div>
        <button className="mr-4 text-dodger-blue-500" onClick={onToggle}>
          <ExpandIcon
            aria-label={expanded ? 'collapse' : 'expand'}
            className={classNames('h-3 w-3 fill-current', { 'rotate-180': !expanded })}
          />
        </button>
      </div>
      {expanded ? <div className="flex flex-col p-6">{children}</div> : null}
    </div>
  );
}
